/*
 * Copyright (c) Nexign, JSC, 2020-2024.
 */
/**
 * Wizards constants
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.32.0
 * @since 1.2.0
 */

import { AccessComponentKeyEnum } from '../role/constants/guard/guardConstants';

export const WIZARD_ALL_ENTITIES = 0;

export enum EWizardName {
    ADDITIONAL_ACCOUNTS = 'additional-accounts',
    ADDITIONAL_REPORTS = 'additional-reports',
    BILLS_DOC = 'bills-doc',
    CALLS_DETAIL = 'calls-detail',
    CHANGE_LOCK = 'change-lock',
    EXPENSES_DETAIL = 'detail-client-expenses',
    IMPORT_PERSONAL_DATA = 'personal-data-import',
    LABELS_IMPORT = 'labels-import',
    MONEY_TRANSFER = 'money-transfer',
    MULTI_SEARCH = 'multi-search',
    PRODUCT_OFFERINGS = 'product-offerings',
    PRODUCT_OFFERINGS_CAMPAIGN = 'product-offerings-campaign',
    ORDER_TEMPLATE = 'order-template',
    ORDER_REPORT = 'order-report',
    COLLATION_REPORT = 'collation-report',
    IMPORT_SUBSCRIBERS_LIMITS = 'limits-import',
    CHANGE_ROLE = 'change-role',
}

export const WIZARD_START_ACTION = '@@b2b_portal_ui/wizard/WIZARD_START';
export const WIZARD_START_SUCCESS = '@@b2b_portal_ui/wizard/WIZARD_START_SUCCESS';
export const WIZARD_START_FAILED = '@@b2b_portal_ui/wizard/WIZARD_START_FAILED';

export const WIZARD_RESTORE_ACTION = '@@b2b_portal_ui/wizard/WIZARD_RESTORE_ACTION';
export const WIZARD_RESTORE_SUCCESS = '@@b2b_portal_ui/wizard/WIZARD_RESTORE_SUCCESS';
export const WIZARD_RESTORE_FAILED = '@@b2b_portal_ui/wizard/WIZARD_RESTORE_FAILED';

export const WIZARD_NEXT_ACTION = '@@b2b_portal_ui/wizard/WIZARD_NEXT_ACTION';
export const WIZARD_NEXT_SUCCESS = '@@b2b_portal_ui/wizard/WIZARD_NEXT_SUCCESS';
export const WIZARD_NEXT_FAILED = '@@b2b_portal_ui/wizard/WIZARD_NEXT_FAILED';

export const WIZARD_BACK_ACTION = '@@b2b_portal_ui/wizard/WIZARD_BACK_ACTION';
export const WIZARD_BACK_SUCCESS = '@@b2b_portal_ui/wizard/WIZARD_BACK_SUCCESS';
export const WIZARD_BACK_FAILED = '@@b2b_portal_ui/wizard/WIZARD_BACK_FAILED';

export const WIZARD_META_ACTION = '@@b2b_portal_ui/wizard/WIZARD_META_ACTION';
export const WIZARD_META_SUCCESS = '@@b2b_portal_ui/wizard/WIZARD_META_SUCCESS';
export const WIZARD_META_FAILED = '@@b2b_portal_ui/wizard/WIZARD_META_FAILED';

export const WIZARD_CANCEL_ACTION = '@@b2b_portal_ui/wizard/WIZARD_CANCEL_ACTION';
export const WIZARD_FINISH_ACTION = '@@b2b_portal_ui/wizard/WIZARD_FINISH_ACTION';

export const WIZARD_STORAGE_CLEAR_STEPS = '@@b2b_portal_ui/wizard/WIZARD_STORAGE_CLEAR_STEPS';
export const WIZARD_STORAGE_CLEAR_STEPS_SUCCESS = '@@b2b_portal_ui/wizard/WIZARD_STORAGE_CLEAR_STEPS_SUCCESS';

export const WIZARD_STORAGE_LOAD_STEPS = '@@b2b_portal_ui/wizard/WIZARD_STORAGE_LOAD_STEPS';
export const WIZARD_STORAGE_SAVE_STEP = '@@b2b_portal_ui/wizard/WIZARD_STORAGE_SAVE_STEP';
export const WIZARD_STORAGE_UPDATE_STEP = '@@b2b_portal_ui/wizard/WIZARD_STORAGE_UPDATE_STEP';

export const WIZARD_LOADER_START = '@@b2b_portal_ui/wizard/WIZARD_LOADER_START';
export const WIZARD_LOADER_STOP = '@@b2b_portal_ui/wizard/WIZARD_LOADER_STOP';

export const WIZARD_INIT_CURRENT_SAGA = '@@b2b_portal_ui/wizard/WIZARD_INIT_CURRENT_SAGA';

export const WIZARD_REPORT_SET_PARAM = '@@b2b_portal_ui/wizard/REPORT_SET_PARAM';
export const WIZARD_REPORT_SET_PARAMS = '@@b2b_portal_ui/wizard/REPORT_SET_PARAMS';
export const WIZARD_REPORT_SET_ERRORS = '@@b2b_portal_ui/wizard/REPORT_SET_ERRORS';
export const WIZARD_REPORT_STORAGE_SAVE = '@@b2b_portal_ui/wizard/REPORT_STORAGE_SAVE';

export const WIZARD_SET_ENTITY_ID = '@@b2b_portal_ui/wizard/SET_ENTITY_ID';

export const WIZARD_OPEN_MODAL_SUMMARY = '@@b2b_portal_ui/wizard/OPEN_MODAL_SUMMARY';

export const WIZARD_SET_IS_REQUESTS_STICKY = '@@b2b_portal_ui/wizard/WIZARD_SET_IS_REQUESTS_STICKY';

export const MapWizardNameToGuardComponentKey: { [key in EWizardName]: AccessComponentKeyEnum } = {
    [EWizardName.ADDITIONAL_ACCOUNTS]: AccessComponentKeyEnum.PageWizardAdditionalAccounts,
    [EWizardName.ADDITIONAL_REPORTS]: AccessComponentKeyEnum.PageWizardAdditionalReports,
    [EWizardName.BILLS_DOC]: AccessComponentKeyEnum.PageWizardBillsDocs,
    [EWizardName.CALLS_DETAIL]: AccessComponentKeyEnum.PageWizardCallsDetail,
    [EWizardName.CHANGE_LOCK]: AccessComponentKeyEnum.PageWizardChangeLock,
    [EWizardName.EXPENSES_DETAIL]: AccessComponentKeyEnum.PageWizardExpensesDetail,
    [EWizardName.IMPORT_PERSONAL_DATA]: AccessComponentKeyEnum.PageWizardImportPersonalData,
    [EWizardName.LABELS_IMPORT]: AccessComponentKeyEnum.PageLabelsImport,
    [EWizardName.MONEY_TRANSFER]: AccessComponentKeyEnum.PageMoneyTransfer,
    [EWizardName.MULTI_SEARCH]: AccessComponentKeyEnum.PageWizardMultiSearch,
    [EWizardName.PRODUCT_OFFERINGS]: AccessComponentKeyEnum.WizardProductOfferings,
    [EWizardName.PRODUCT_OFFERINGS_CAMPAIGN]: AccessComponentKeyEnum.WizardProductOfferings,
    [EWizardName.ORDER_TEMPLATE]: AccessComponentKeyEnum.PageWizardOrderTemplate,
    [EWizardName.ORDER_REPORT]: AccessComponentKeyEnum.PageWizardOrderReport,
    [EWizardName.COLLATION_REPORT]: AccessComponentKeyEnum.PageWizardCollationReport,
    [EWizardName.IMPORT_SUBSCRIBERS_LIMITS]: AccessComponentKeyEnum.PageWizardImportSubscribersLimits,
    [EWizardName.CHANGE_ROLE]: AccessComponentKeyEnum.PageWizardChangeRole,
};
